import type { GenderTotals, QuarterData, YearlyAggregates } from './types'

// ── Generic calculation helpers shared across study pages ─────────────────
// All functions are stateless and side-effect free.

export type { QuarterData, GenderTotals, YearlyAggregates, StudyStats } from './types'

/** Display-name mapping for recognised payer type codes. */
export const PAY_LABEL: Record<string, string> = {
  MDCR: 'Medicare',
  PBM: 'PBM',
  EMP_GRP: 'Employer Group',
  UNSP: 'Unspecified',
  COMM: 'Commercial',
  MDCD: 'Medicaid',
  CASH: 'Cash Pay',
}

// ── Row shape constraints ──────────────────────────────────────────────────

type QuarterlyAggRow = {
  total_claims: string
  total_pay: string
  total_patient_pay: string
  estimated_plan_pay: string
  quarter: string
  manufacturers?: string
}

type YearlyAggRow = {
  total_claims: string
  total_pay: string
  patient_state: string
  drugs: string
  plan_types: string
}

type ManufacturerYearlyRow = {
  year?: string
  manufacturers?: string
}

// ── Aggregation functions ──────────────────────────────────────────────────

/**
 * Aggregates raw yearly CSV rows into state, drug, and payer maps plus
 * running totals for claims and spend.  Payer entries whose code is not in
 * PAY_LABEL are silently ignored.
 */
export function aggregateYearlyRows(rows: YearlyAggRow[]): YearlyAggregates {
  const stateDataMap = new Map<string, number>()
  const drugsMap = new Map<string, number>()
  const payMap = new Map<string, number>()
  let totalClaims = 0
  let totalSpend = 0

  for (const row of rows) {
    const claims = Number(row.total_claims)
    const pay = Number(row.total_pay)
    const { patient_state } = row

    totalClaims += claims
    totalSpend += pay

    stateDataMap.set(patient_state, (stateDataMap.get(patient_state) ?? 0) + claims)

    for (const [name, count] of JSON.parse(row.drugs) as [string, number][]) {
      drugsMap.set(name, (drugsMap.get(name) ?? 0) + count)
    }

    for (const [name, count] of JSON.parse(row.plan_types) as [string, number][]) {
      if (PAY_LABEL[name]) {
        payMap.set(name, (payMap.get(name) ?? 0) + count)
      }
    }
  }

  return { totalClaims, totalSpend, stateDataMap, drugsMap, payMap }
}

/**
 * Aggregates raw quarterly CSV rows into a map keyed by quarter string.
 * Rows sharing the same quarter are summed together.
 */
export function aggregateQuarterlyRows(rows: QuarterlyAggRow[]): Map<string, QuarterData> {
  const quarterlyDataMap = new Map<string, QuarterData>()

  for (const row of rows) {
    const claims = Number(row.total_claims)
    const totalPay = Number(row.total_pay)
    const patientPay = Number(row.total_patient_pay)
    const planPay = Number(row.estimated_plan_pay)
    const { quarter } = row

    const mfrEntries: [string, number][] = row.manufacturers ? JSON.parse(row.manufacturers) : []

    const existing = quarterlyDataMap.get(quarter)
    if (existing) {
      existing.claims += claims
      existing.total_pay += totalPay
      existing.patient_pay += patientPay
      existing.plan_pay += planPay
      for (const [mfr, count] of mfrEntries) {
        existing.manufacturerClaims[mfr] = (existing.manufacturerClaims[mfr] ?? 0) + count
      }
    } else {
      const manufacturerClaims: Record<string, number> = {}
      for (const [mfr, count] of mfrEntries) {
        manufacturerClaims[mfr] = (manufacturerClaims[mfr] ?? 0) + count
      }
      quarterlyDataMap.set(quarter, {
        claims,
        total_pay: totalPay,
        patient_pay: patientPay,
        plan_pay: planPay,
        manufacturerClaims,
      })
    }
  }

  return quarterlyDataMap
}

/**
 * Maps raw gender pairs (F/M/UNKNOWN/U/…) from the patient-totals CSV
 * into named totals.  Any key that is not "F" or "M" rolls into unknown.
 */
export function parseGenderTotals(genRaw: [string, number][]): GenderTotals {
  let female = 0
  let male = 0
  let unknown = 0
  for (const [key, count] of genRaw) {
    if (key === 'F') female += count
    else if (key === 'M') male += count
    else unknown += count
  }
  return { female, male, unknown }
}

/**
 * Converts raw birth-year decade pairs ("1960-1969" → "1960s") from the
 * patient-totals CSV into a sorted array of [label, count] tuples.
 * Entries with the key "UNKNOWN" are excluded.
 */
export function parseAgeCohorts(ageRaw: [string, number][]): [string, number][] {
  const map = new Map<string, number>()
  for (const [range, count] of ageRaw) {
    if (range === 'UNKNOWN') continue
    const decade = `${range.slice(0, 4)}s`
    map.set(decade, (map.get(decade) ?? 0) + count)
  }
  return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]))
}

/**
 * Calculates the percentage growth between the first and last quarter's
 * claim count.  Returns 0 when firstClaims is 0 to avoid division by zero.
 */
export function calcClaimsGrowthPct(firstClaims: number, lastClaims: number): number {
  if (firstClaims === 0) return 0
  return ((lastClaims - firstClaims) / firstClaims) * 100
}

/** Quarter keys whose ISO date falls in the given calendar year. */
export function quarterKeysInCalendarYear(sortedQuarterKeys: string[], year: number): string[] {
  const prefix = `${year}-`
  return sortedQuarterKeys.filter((k) => k.startsWith(prefix))
}

type QuarterMetricRow = { total_pay: number; claims: number }

/**
 * Mean of `metric` across quarters in `year` (national quarterly series).
 */
export function avgQuarterlyMetricInYear(
  qd: Record<string, QuarterMetricRow>,
  sortedQuarterKeys: string[],
  year: number,
  metric: 'total_pay' | 'claims',
): number {
  const qs = quarterKeysInCalendarYear(sortedQuarterKeys, year)
  if (qs.length === 0) return 0
  const sum = qs.reduce((s, k) => s + (qd[k]?.[metric] ?? 0), 0)
  return sum / qs.length
}

/** Highest quarterly claim count within a calendar year. */
export function maxQuarterlyClaimsInYear(
  qd: Record<string, { claims: number }>,
  sortedQuarterKeys: string[],
  year: number,
): number {
  const qs = quarterKeysInCalendarYear(sortedQuarterKeys, year)
  if (qs.length === 0) return 0
  return Math.max(...qs.map((k) => qd[k]?.claims ?? 0))
}

/**
 * First quarter (in `sortedQuarterKeys` order) whose national `total_pay` is
 * at least `minTotalPay`, with the immediately preceding quarter for comparison.
 * `sortedQuarterKeys` should be the same chronological order as the loader.
 */
export function findFirstQuarterAtLeastTotalPay(
  qd: Record<string, { total_pay: number }>,
  sortedQuarterKeys: string[],
  minTotalPay: number,
): {
  key: string
  prevKey: string | null
  totalPay: number
  prevTotalPay: number | null
} | null {
  for (let i = 0; i < sortedQuarterKeys.length; i++) {
    const k = sortedQuarterKeys[i]
    const pay = qd[k]?.total_pay ?? 0
    if (pay >= minTotalPay) {
      const prevKey = i > 0 ? sortedQuarterKeys[i - 1] : null
      return {
        key: k,
        prevKey,
        totalPay: pay,
        prevTotalPay: prevKey != null ? (qd[prevKey]?.total_pay ?? null) : null,
      }
    }
  }
  return null
}

/**
 * Returns the top `n` entries from any [key, number] map, sorted
 * descending by value.
 */
export function getTopEntries<K>(map: Map<K, number>, n: number): [K, number][] {
  return [...map.entries()].sort((a, b) => b[1] - a[1]).slice(0, n) as [K, number][]
}

/**
 * Aggregates raw yearly CSV rows into a map of year → manufacturer → claims.
 * The year field is expected to be an ISO date ("YYYY-01-01"); only the
 * four-digit year portion is used as the key.
 */
export function aggregateManufacturerYearlyData(
  rows: ManufacturerYearlyRow[],
): Map<string, Record<string, number>> {
  const yearMfrMap = new Map<string, Record<string, number>>()

  for (const row of rows) {
    if (!row.year) continue
    const yearKey = row.year.slice(0, 4)
    const mfrEntries: [string, number][] = row.manufacturers ? JSON.parse(row.manufacturers) : []

    const existing = yearMfrMap.get(yearKey)
    if (existing) {
      for (const [mfr, count] of mfrEntries) {
        existing[mfr] = (existing[mfr] ?? 0) + count
      }
    } else {
      const mfrData: Record<string, number> = {}
      for (const [mfr, count] of mfrEntries) {
        mfrData[mfr] = (mfrData[mfr] ?? 0) + count
      }
      yearMfrMap.set(yearKey, mfrData)
    }
  }

  return yearMfrMap
}

/**
 * Builds year → manufacturer → claims from manufacturer × quarter rows by
 * summing `total_claims` into calendar years (`quarter` ISO date, YYYY-MM-DD).
 */
export function aggregateManufacturerYearlyFromQuarterly(
  rows: { manufacturer: string; quarter: string; total_claims: string }[],
): Map<string, Record<string, number>> {
  const yearMfrMap = new Map<string, Record<string, number>>()

  for (const row of rows) {
    if (!row.manufacturer || !row.quarter) continue
    const yearKey = row.quarter.slice(0, 4)
    const claims = Number(row.total_claims ?? 0)
    const existing = yearMfrMap.get(yearKey)
    if (existing) {
      existing[row.manufacturer] = (existing[row.manufacturer] ?? 0) + claims
    } else {
      yearMfrMap.set(yearKey, { [row.manufacturer]: claims })
    }
  }

  return yearMfrMap
}

/**
 * Sums `total_pay` by calendar year and manufacturer from manufacturer × quarter rows
 * (`quarter` ISO date → YYYY). Used for yearly market spend breakdowns.
 */
export function aggregateManufacturerYearlyPayFromQuarterly(
  rows: { manufacturer: string; quarter: string; total_pay: string }[],
): Map<string, Record<string, number>> {
  const yearMfrMap = new Map<string, Record<string, number>>()

  for (const row of rows) {
    if (!row.manufacturer || !row.quarter) continue
    const yearKey = row.quarter.slice(0, 4)
    const pay = Number(row.total_pay ?? 0)
    const existing = yearMfrMap.get(yearKey)
    if (existing) {
      existing[row.manufacturer] = (existing[row.manufacturer] ?? 0) + pay
    } else {
      yearMfrMap.set(yearKey, { [row.manufacturer]: pay })
    }
  }

  return yearMfrMap
}

// ── Display / component helpers ────────────────────────────────────────────

/**
 * Converts an ISO date string (first day of a quarter, "YYYY-MM-DD") into a
 * human-readable quarter label like "Q1 2015".
 *
 * Parses the year and month directly from the string to avoid timezone
 * shifting: `new Date('2015-01-01')` is treated as UTC midnight and would
 * resolve to December 2014 in US timezones.
 */
export function formatQuarterLabel(isoDate: string): string {
  const [year, month] = isoDate.split('-').map(Number)
  const quarter = Math.floor((month - 1) / 3) + 1
  return `Q${quarter} ${year}`
}

/**
 * Sums claim counts for all drug entries whose name is in `names`.
 * Matching is case-sensitive (drug names are stored in UPPER CASE).
 */
export function sumClaimsByNames(drugs: [string, number][], names: readonly string[]): number {
  const nameSet = new Set(names)
  return drugs.filter(([name]) => nameSet.has(name)).reduce((sum, [, count]) => sum + count, 0)
}

/**
 * Returns the share of `part` within `total` as a number in [0, 100].
 * Returns 0 when `total` is 0 to avoid division by zero.
 */
export function calcSharePct(part: number, total: number): number {
  if (total === 0) return 0
  return (part / total) * 100
}

/**
 * Calculates the percentage of total payer volume that is NOT cash-pay
 * (i.e. the insured/covered share).
 */
export function calcInsuredSpendPct(cashPay: number, totalPayVol: number): number {
  return 100 - calcSharePct(cashPay, totalPayVol)
}

/**
 * Returns the [label, count] entry with the highest count from a list of
 * age cohort pairs.  Assumes the list is non-empty.
 */
export function findTopAgeCohort(age: [string, number][]): [string, number] {
  return [...age].sort((a, b) => b[1] - a[1])[0]
}

/**
 * Formats a raw count to a compact string with the appropriate suffix based
 * on magnitude:
 *   - < 1,000       → integer          (e.g. 850  → "850")
 *   - 1,000–9,999   → one decimal + K  (e.g. 2500 → "2.5K")
 *   - 10,000–999,999→ no decimal + K   (e.g. 45000 → "45K")
 *   - 1M–9.9M       → one decimal + M  (e.g. 1200000 → "1.2M")
 *   - ≥ 10M         → no decimal + M   (e.g. 40000000 → "40M")
 */
export function formatCount(value: number): string {
  if (value >= 1_000_000_000_000) {
    const t = value / 1_000_000_000_000
    return (t >= 10 ? t.toFixed(0) : t.toFixed(1)) + 'T'
  }
  if (value >= 1_000_000_000) {
    const b = value / 1_000_000_000
    return (b >= 10 ? b.toFixed(0) : b.toFixed(1)) + 'B'
  }
  if (value >= 1_000_000) {
    const m = value / 1_000_000
    return (m >= 10 ? m.toFixed(0) : m.toFixed(1)) + 'M'
  }
  if (value >= 1_000) {
    const k = value / 1_000
    return (k >= 10 ? k.toFixed(0) : k.toFixed(1)) + 'K'
  }
  return String(Math.round(value))
}

/**
 * Formats a raw dollar amount to a compact string with $ and appropriate suffix
 * (K/M/B/T), matching the scale rules used by formatCount. Use for spend,
 * revenue, or other currency values.
 *   - < 1,000       → "$850"
 *   - 1K–9.9K      → "$2.5K"
 *   - 10K–999K     → "$45K", "$500K"
 *   - 1M–9.9M      → "$1.2M"
 *   - ≥ 10M        → "$40M", "$1.2B"
 */
export function formatSpend(value: number): string {
  if (value >= 1_000_000_000_000) {
    const t = value / 1_000_000_000_000
    return '$' + (t >= 10 ? t.toFixed(0) : t.toFixed(1)) + 'T'
  }
  if (value >= 1_000_000_000) {
    const b = value / 1_000_000_000
    return '$' + (b >= 10 ? b.toFixed(0) : b.toFixed(1)) + 'B'
  }
  if (value >= 1_000_000) {
    const m = value / 1_000_000
    return '$' + (m >= 10 ? m.toFixed(0) : m.toFixed(1)) + 'M'
  }
  if (value >= 1_000) {
    const k = value / 1_000
    return '$' + (k >= 10 ? k.toFixed(0) : k.toFixed(1)) + 'K'
  }
  return '$' + Math.round(value)
}
